// src/context/persona/assist/CreatePersonaForm.jsx — "Generala con l'AI" (v197, /tenant-admin/personas only).
// Name · market · gender · age range · optional "who" / "pain & gain" notes · documents → a background create job
// (`onStart(body, name)`; the parent starts it through usePersonaJob so the running line can name it).
// ⛔ NOT a form element (decision 9): Enter in an input does nothing, only the button starts the job; the two
// textareas keep Enter for new lines. Problems (assistModel.createProblems) show only after a start attempt.
// `initial` pre-fills name / market (a Propose "new" row).
// v198 — a STEP of the persona modal ("Brief"): no panel chrome and no title (the modal has them), the button in
// <ModalFooter> (no Annulla: the modal's × / Esc close it). BASE = name, market, "Chi è", documents; AVANZATE
// (the page preference, `advanced` / `onAdvancedChange`) = "Problemi e obiettivi", gender, age range. A problem on a hidden
// field forces Avanzate open after a start attempt (an override for this form only, never persisted). A start
// refusal (`startError`) shows on top through StartNotice. Reports `onDirtyChange` (anything typed, changed or
// ticked) so the modal can ask before closing.
// Each field's explanation is an ⓘ tooltip (components/ui/InfoTip) and the text fields carry an example
// placeholder; required fields (Nome, Mercato) carry a red "*".
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentPicker from '../../DocumentPicker.jsx';
import AdvancedToggle from '../../../components/ui/AdvancedToggle.jsx';
import InfoTip from '../../../components/ui/InfoTip.jsx';
import { COLORS, SEMANTIC } from '../../../constants/theme.js';
import { ModalFooter } from '../../../components/ui/WizardModal.jsx';
import {
  choiceHint, fieldError, fieldName, footerRow, primaryBtn, sectionTextarea, textInput,
} from '../personaStyles.js';
import {
  AGE_MAX, AGE_MIN, GENDERS, MARKETS, NAME_MAX, TEXT_MAX, buildCreateBody, createProblems,
} from './assistModel.js';
import { StartNotice } from './JobCard.jsx';

const AGES = Array.from({ length: AGE_MAX - AGE_MIN + 1 }, (_, i) => String(AGE_MIN + i));
const selectStyle = { ...textInput, maxWidth: '260px' };
const ageRow = { display: 'flex', gap: '16px', flexWrap: 'wrap' };
const block = { marginBottom: '14px' };
const headRow = { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' };
const requiredMark = { marginLeft: '-3px', color: SEMANTIC.errore.fg, fontSize: '12px', fontWeight: 700 };
const subLabel = { display: 'block', fontSize: '12px', color: COLORS.textSecondary, marginBottom: '4px' };
// The fields shown only with Avanzate on (gender has no problem rule, so it never forces the reveal).
const ADVANCED_KEYS = ['painGain', 'ageMin', 'ageMax'];

// A field's header: its label, a red "*" when required (the control also carries aria-required), and the ⓘ
// tooltip explaining the field — the explanations live in tooltips so this long form stays light.
// `htmlFor` ties the label to its control; a group heading (Età) passes `id` instead.
function FieldHead({ t, htmlFor, id, label, required = false, tip }) {
  const Tag = htmlFor ? 'label' : 'span';
  return (
    <div style={headRow}>
      <Tag id={id} htmlFor={htmlFor} style={{ ...fieldName, marginBottom: 0 }}>{label}</Tag>
      {/* Beside the label, not inside it: the label's text stays the field's exact name. */}
      {required && <span aria-hidden="true" style={requiredMark}>*</span>}
      <InfoTip label={t('personas.assist.infoAbout', { field: label })} text={t(`personas.assist.createTips.${tip}`)} />
    </div>
  );
}

function Problem({ t, id, reason }) {
  if (!reason) return null;
  return <span id={id} style={fieldError}>{t(`personas.assist.problems.${reason}`)}</span>;
}

export default function CreatePersonaForm({
  tenantId, initial = {}, busy = false, onStart, startError = null, onDismissStartError,
  advanced = false, onAdvancedChange, onDirtyChange,
}) {
  const { t } = useTranslation('context');
  const [form, setForm] = useState(() => ({
    name: typeof initial.name === 'string' ? initial.name : '',
    market: MARKETS.includes(initial.market) ? initial.market : 'b2b',
    gender: 'any',
    ageMin: String(AGE_MIN),
    ageMax: '',
    who: '',
    painGain: '',
  }));
  const [selected, setSelected] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [forcedOpen, setForcedOpen] = useState(false);
  const shownAdvanced = advanced || forcedOpen;

  const problems = useMemo(() => createProblems(form), [form]);
  const shown = attempted ? problems : {};
  const set = (key) => (ev) => setForm((prev) => ({ ...prev, [key]: ev.target.value }));
  const disabled = busy || pickerBusy;

  const initialName = typeof initial.name === 'string' ? initial.name : '';
  const dirty = form.name !== initialName || form.who !== '' || form.painGain !== '' || selected.length > 0
    || form.gender !== 'any' || form.ageMin !== String(AGE_MIN) || form.ageMax !== '';
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  function start() {
    if (disabled) return;
    setAttempted(true);
    if (Object.keys(problems).length > 0) {
      if (ADVANCED_KEYS.some((k) => problems[k])) setForcedOpen(true);
      return;
    }
    onStart(buildCreateBody(form, selected), form.name.trim());
  }

  function toggleAdvanced(on) {
    setForcedOpen(false);
    if (onAdvancedChange) onAdvancedChange(on);
  }
  const hidden = shownAdvanced ? 0 : ADVANCED_KEYS.filter((k) => shown[k]).length;

  const invalid = (key) => (shown[key] ? true : undefined);
  const describedBy = (key) => (shown[key] ? `pc-${key}-problem` : undefined);

  return (
    <div data-testid="persona-create-form">
      <StartNotice startError={startError} onDismiss={onDismissStartError} />

      <div style={block}>
        <FieldHead t={t} htmlFor="pc-name" label={t('personas.assist.createName')} required tip="name" />
        <input
          id="pc-name" type="text" value={form.name} maxLength={NAME_MAX} onChange={set('name')} disabled={busy}
          aria-required="true" aria-invalid={invalid('name')} aria-describedby={describedBy('name')}
          placeholder={t('personas.assist.createPlaceholders.name')} style={{ ...textInput, marginTop: 0 }}
        />
        <Problem t={t} id="pc-name-problem" reason={shown.name} />
      </div>

      <div style={block}>
        <FieldHead t={t} htmlFor="pc-market" label={t('personas.assist.createMarket')} required tip="market" />
        <select
          id="pc-market" value={form.market} onChange={set('market')} disabled={busy}
          aria-required="true" aria-invalid={invalid('market')} aria-describedby={describedBy('market')}
          style={{ ...selectStyle, marginTop: 0 }}
        >
          {MARKETS.map((m) => <option key={m} value={m}>{t(`personas.markets.${m}`)}</option>)}
        </select>
        <Problem t={t} id="pc-market-problem" reason={shown.market} />
      </div>

      <div style={block}>
        <FieldHead t={t} htmlFor="pc-who" label={t('personas.assist.who')} tip="who" />
        <textarea
          id="pc-who" rows={3} value={form.who} maxLength={TEXT_MAX} onChange={set('who')} disabled={busy}
          aria-invalid={invalid('who')} aria-describedby={describedBy('who')}
          placeholder={t('personas.assist.createPlaceholders.who')} style={sectionTextarea(busy)}
        />
        <Problem t={t} id="pc-who-problem" reason={shown.who} />
      </div>

      <div style={block}>
        <AdvancedToggle
          on={shownAdvanced} onChange={toggleAdvanced} label={t('personas.advanced.label')}
          hiddenCount={hidden} hiddenLabel={t('personas.advanced.hidden', { count: hidden })}
        />
      </div>

      {shownAdvanced && (
        <>
          <div style={block}>
            <FieldHead t={t} htmlFor="pc-painGain" label={t('personas.assist.painGain')} tip="painGain" />
            <textarea
              id="pc-painGain" rows={3} value={form.painGain} maxLength={TEXT_MAX} onChange={set('painGain')} disabled={busy}
              aria-invalid={invalid('painGain')} aria-describedby={describedBy('painGain')}
              placeholder={t('personas.assist.createPlaceholders.painGain')} style={sectionTextarea(busy)}
            />
            <Problem t={t} id="pc-painGain-problem" reason={shown.painGain} />
          </div>
          <div style={block}>
            <FieldHead t={t} htmlFor="pc-gender" label={t('personas.assist.gender')} tip="gender" />
            <select id="pc-gender" value={form.gender} onChange={set('gender')} disabled={busy} style={{ ...selectStyle, marginTop: 0 }}>
              {GENDERS.map((g) => <option key={g} value={g}>{t(`personas.assist.genders.${g}`)}</option>)}
            </select>
          </div>

          {/* One "Età" heading (with its tip) over the two selects; each select keeps its full name
              ("Età da" / "Età fino a") for screen readers, while the visible sub-labels are short. */}
          <div role="group" aria-labelledby="pc-age-title" style={block}>
            <FieldHead t={t} id="pc-age-title" label={t('personas.assist.ageTitle')} tip="age" />
            <div style={ageRow}>
              <div>
                <span style={subLabel}>{t('personas.assist.ageFrom')}</span>
                <select
                  aria-label={t('personas.assist.ageMin')} value={form.ageMin} onChange={set('ageMin')} disabled={busy}
                  aria-invalid={invalid('ageMin')} aria-describedby={describedBy('ageMin')} style={{ ...selectStyle, marginTop: 0 }}
                >
                  {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <Problem t={t} id="pc-ageMin-problem" reason={shown.ageMin} />
              </div>
              <div>
                <span style={subLabel}>{t('personas.assist.ageTo')}</span>
                <select
                  aria-label={t('personas.assist.ageMax')} value={form.ageMax} onChange={set('ageMax')} disabled={busy}
                  aria-invalid={invalid('ageMax')} aria-describedby={describedBy('ageMax')} style={{ ...selectStyle, marginTop: 0 }}
                >
                  <option value="">{t('personas.assist.agePlus')}</option>
                  {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <Problem t={t} id="pc-ageMax-problem" reason={shown.ageMax} />
              </div>
            </div>
          </div>

        </>
      )}

      {/* The documents section: the same title and visible hint as in "Adatta" (PersonalizeControl). */}
      <div style={{ marginBottom: '8px' }}>
        <h3 style={{ ...fieldName, margin: '0 0 6px' }}>{t('personas.assist.referenceDocsTitle')}</h3>
        <p style={{ ...choiceHint, marginTop: 0, marginBottom: '10px' }}>{t('personas.assist.referenceDocsHint')}</p>
      </div>
      <DocumentPicker
        tenantId={tenantId} label={t('personas.assist.docsLabel')} selected={selected} onChange={setSelected}
        disabled={busy} onBusyChange={setPickerBusy}
      />

      <ModalFooter>
        <div style={footerRow}>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={disabled} onClick={start} style={primaryBtn(disabled)}>
            {t('personas.assist.createSubmit')}
          </button>
        </div>
      </ModalFooter>
    </div>
  );
}
